import {
  Box,
  Button,
  Card,
  FormControl,
  FormGroup,
  Grid,
  IconButton,
  Modal,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect } from "react";
import CloseIcon from "@mui/icons-material/Close";
import { SubmitHandler, useForm } from "react-hook-form";
import { ReportQuarterTableColumn, QuarterRow } from "../../../constants";

interface Props {
  isOpened: boolean;
  onClose: () => void;
  quarterRow?: QuarterRow;
  onSubmit: (data: QuarterRow) => void;
}

interface QuarterRowInputs {
  [ReportQuarterTableColumn.Period]: string;
  [ReportQuarterTableColumn.StockPrice]: number;
  [ReportQuarterTableColumn.OptionIncome]: number;
  [ReportQuarterTableColumn.DividenIncome]: number;
  [ReportQuarterTableColumn.CumulativeIncome]: number;
  [ReportQuarterTableColumn.Comment]: string;
}

const defaultValues: QuarterRowInputs = {
  [ReportQuarterTableColumn.Period]: "",
  [ReportQuarterTableColumn.StockPrice]: 0,
  [ReportQuarterTableColumn.OptionIncome]: 0,
  [ReportQuarterTableColumn.DividenIncome]: 0,
  [ReportQuarterTableColumn.CumulativeIncome]: 0,
  [ReportQuarterTableColumn.Comment]: "",
};

export const QuarterTableEditModal = ({
  isOpened,
  onClose,
  quarterRow,
  onSubmit,
}: Props) => {
  const { register, handleSubmit, reset } = useForm<QuarterRowInputs>({
    defaultValues,
  });

  const closeModal = () => {
    onClose();
    reset();
  };

  const handleFormSubmit: SubmitHandler<QuarterRowInputs> = (data) => {
    if (quarterRow) {
      onSubmit({
        ...data,
        id: quarterRow.id,
      });
    }
    closeModal();
  };

  useEffect(() => {
    if (quarterRow) {
      reset({
        [ReportQuarterTableColumn.Period]:
          quarterRow[ReportQuarterTableColumn.Period],
        [ReportQuarterTableColumn.StockPrice]:
          quarterRow[ReportQuarterTableColumn.StockPrice],
        [ReportQuarterTableColumn.OptionIncome]:
          quarterRow[ReportQuarterTableColumn.OptionIncome],
        [ReportQuarterTableColumn.DividenIncome]:
          quarterRow[ReportQuarterTableColumn.DividenIncome],
        [ReportQuarterTableColumn.CumulativeIncome]:
          quarterRow[ReportQuarterTableColumn.CumulativeIncome],
        [ReportQuarterTableColumn.Comment]:
          quarterRow[ReportQuarterTableColumn.Comment],
      });
    } else {
      reset({ ...defaultValues });
    }
  }, [quarterRow, reset]);

  return (
    <Modal open={isOpened} onClose={closeModal}>
      <Stack height="100%" alignItems="center" justifyContent="center">
        <Box
          style={{ width: "600px" }}
          component="form"
          onSubmit={handleSubmit(handleFormSubmit)}
        >
          <Card>
            <Grid container justifyContent="space-between" alignItems="center">
              <Grid item>
                <Typography variant="h5" component="h5" sx={{ padding: 1 }}>
                  Edit Quarter Data
                </Typography>
              </Grid>
              <Grid item>
                <IconButton onClick={closeModal} aria-label="Close modal">
                  <CloseIcon />
                </IconButton>
              </Grid>
            </Grid>
            <FormGroup>
              <FormControl sx={{ margin: 1 }}>
                <TextField
                  label={ReportQuarterTableColumn.Period}
                  {...register(ReportQuarterTableColumn.Period, {
                    required: true,
                  })}
                />
              </FormControl>
              <FormControl sx={{ margin: 1 }}>
                <TextField
                  label={ReportQuarterTableColumn.StockPrice}
                  type="number"
                  inputProps={{ step: "0.01" }}
                  {...register(ReportQuarterTableColumn.StockPrice, {
                    required: true,
                    valueAsNumber: true,
                  })}
                />
              </FormControl>
              <FormControl sx={{ margin: 1 }}>
                <TextField
                  label={ReportQuarterTableColumn.OptionIncome}
                  type="number"
                  {...register(ReportQuarterTableColumn.OptionIncome, {
                    required: true,
                    valueAsNumber: true,
                  })}
                />
              </FormControl>
              <FormControl sx={{ margin: 1 }}>
                <TextField
                  label={ReportQuarterTableColumn.DividenIncome}
                  type="number"
                  {...register(ReportQuarterTableColumn.DividenIncome, {
                    required: true,
                    valueAsNumber: true,
                  })}
                />
              </FormControl>
              <FormControl sx={{ margin: 1 }}>
                <TextField
                  label={ReportQuarterTableColumn.CumulativeIncome}
                  type="number"
                  {...register(ReportQuarterTableColumn.CumulativeIncome, {
                    required: true,
                    valueAsNumber: true,
                  })}
                />
              </FormControl>
              <FormControl sx={{ margin: 1 }}>
                <TextField
                  label={ReportQuarterTableColumn.Comment}
                  multiline
                  rows={3}
                  {...register(ReportQuarterTableColumn.Comment)}
                />
              </FormControl>
              <Button type="submit" variant="contained" sx={{ margin: 1 }}>
                Save
              </Button>
            </FormGroup>
          </Card>
        </Box>
      </Stack>
    </Modal>
  );
};
